import {
  ChevronRight,
  Plane,
  Users,
  Briefcase,
  Car,
  Award,
  MapPin,
  Phone,
  Mail,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  ChevronDown,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './components/Header';
import FlagsCarousel from './components/FlagsCarousel';
import NewsletterForm from './components/NewsletterForm';
import Footer from './components/Footer';
import { OrganizationSchema } from './components/SchemaMarkup';

function App() {
  const [showMore, setShowMore] = useState(false);

  return (
    <div className="font-[Arial] text-black">
      <OrganizationSchema />
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#000000] to-[#1a3c55] text-white py-16 md:py-24">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center">
          <div className="w-full md:w-1/2 mb-10 md:mb-0 animate-fadeInLeft">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 pt-5">
              SEAMLESS TRAVEL,
              <br />
              EXCEPTIONAL SERVICE
            </h1>
            <p className="text-lg mb-8">
              Effortless <Link to="/services">airport</Link> experiences with <Link to="/services"> 
              premium travel and guest</Link>
              <br />
              lounge, chauffeur, and <Link to="/services">baggage assistance</Link> services.
            </p>
            <div className="flex space-x-4 pt-5">
              <Link to="/contact" className="bg-[#73BDF2] text-white px-6 py-3 rounded hover:bg-[#5ba8e0] transition-colors duration-300 flex items-center">
                Book Now <ChevronRight size={20} className="ml-2" />
              </Link>
              <Link to="/services" className="border border-[#73BDF2] text-[#73BDF2] px-6 py-3 rounded hover:bg-[#73BDF2]/10 transition-colors duration-300 flex items-center">
                Learn More <ChevronRight size={20} className="ml-2" />
              </Link>
            </div>
            <div className="mt-12">
              <p className="mt-6 pb-5 text-sm">Present in 135+ Countries</p>
              <div className="hidden md:block">
                <FlagsCarousel />
              </div>
            </div>
          </div>
          <div className="md:w-1/2 relative animate-fadeInRight">
            <div className="relative z-10 rounded-lg overflow-hidden border-4 border-[#73BDF2] shadow-xl">
            <img
              src="https://images.unsplash.com/photo-1619659085779-29fcc0e41110?q=80&w=1470&auto=format&fit=crop"
              alt="Luxury private jet parked at an international airport during daytime"
              className="w-full h-auto"
              loading="lazy"
            />
            </div>
            <div className="absolute top-1/2 right-0 transform translate-x-1/4 -translate-y-1/4 rounded-lg overflow-hidden border-4 border-[#73BDF2] shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1521791055366-0d553872125f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1169&q=80"
                alt="Airport service"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/4 bg-[#73BDF2] rounded-full w-12 h-12 flex items-center justify-center shadow-lg cursor-pointer hover:bg-[#5ba8e0] transition-colors duration-300">
              <ChevronDown size={24} className="text-white animate-bounce" />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0 animate-fadeInLeft">
            <img
              src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
              alt="Professional business handshake at an airport lounge"
              className="rounded-lg border-4 border-[#73BDF2] shadow-xl"
              loading="lazy"
            />
              <div className="bg-[#73BDF2] text-white p-6 rounded-lg shadow-lg -mt-16 ml-16 relative z-10 animate-fadeIn">
                <div className="flex items-center justify-center">
                  <span className="text-5xl font-bold">5</span>
                  <div className="ml-2">
                    <p className="text-sm">Years Of</p>
                    <p className="font-bold">Experience</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:w-1/2 md:pl-12 animate-fadeInRight">
              <p className="text-[#73BDF2] font-bold mb-2">ABOUT US</p>
              <h2 className="text-3xl font-bold mb-6">
                REDEFINING YOUR AIRPORT EXPERIENCE
              </h2>
              <p className="mb-6">
                At Moreflex Aviation, <Link to="/about">we understand the stress that comes with
                travel.</Link> That's why we offer Airport Premium Support Assistance,
                ensuring your journey is smooth, comfortable, and hassle-free
                from arrival to departure.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/20 p-2 rounded-full mr-4">
                    <Users className="text-[#73BDF2]" />
                  </div>
                  <div>
                    <h3 className="font-bold">Personalized Assistance</h3>
                    <p className="text-gray-600">
                      Tailored services to meet your specific needs
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/20 p-2 rounded-full mr-4">
                    <Award className="text-[#73BDF2]" />
                  </div>
                  <div>
                    <h3 className="font-bold">Luxury & Comfort</h3>
                    <p className="text-gray-600">
                      Premium lounges and exclusive amenities
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/20 p-2 rounded-full mr-4">
                    <Briefcase className="text-[#73BDF2]" />
                  </div>
                  <div>
                    <h3 className="font-bold">Experienced Professionals</h3>
                    <p className="text-gray-600">
                      Highly trained staff dedicated to your satisfaction
                    </p>
                  </div>
                </div>
              </div>
              <Link to="/About">
                <button className="mt-8 bg-[#73BDF2] text-white px-6 py-3 rounded hover:bg-[#5ba8e0] transition-colors duration-300 flex items-center">
                  Learn More <ChevronRight size={20} className="ml-2" />
                </button>
              </Link>

            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold">
            <Link to="/services">Explore our
              <br />
              services</Link>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="bg-[#73BDF2] rounded-lg overflow-hidden shadow-lg transform hover:-translate-y-2 transition-transform duration-300 animate-fadeIn">
              <div className="p-8 text-white">
                <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Users size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4">
                  Meet and Greet Service
                </h3>
                <p className="mb-6">
                  Personalized welcome and assistance throughout your airport
                  journey.
                </p>
                <Link to="/Services">
                <button className="bg-white text-[#73BDF2] px-4 py-2 rounded hover:bg-gray-100 transition-colors duration-300 flex items-center">
                  Read More <ChevronRight size={20} className="ml-2" />
                </button>
                </Link>
              </div>
            </div>

            {/* Service 2 */}
            <div className="bg-gradient-to-r from-[#73BDF2] to-[#000000] rounded-lg overflow-hidden shadow-lg transform hover:-translate-y-2 transition-transform duration-300 animate-fadeIn delay-100">
              <div className="p-8 text-white">
                <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Award size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4">Lounge Services</h3>
                <p className="mb-6">
                  Exclusive access to premium lounges for comfort and
                  relaxation.
                </p>
                <Link to="/Services">
                <button className="bg-white text-[#73BDF2] px-4 py-2 rounded hover:bg-gray-100 transition-colors duration-300 flex items-center">
                  Read More <ChevronRight size={20} className="ml-2" />
                </button>
                </Link>
              </div>
            </div>

            {/* Service 3 */}
            <div className="bg-[#73BDF2] rounded-lg overflow-hidden shadow-lg transform hover:-translate-y-2 transition-transform duration-300 animate-fadeIn delay-200">
              <div className="p-8 text-white">
                <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Car size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4">Fleet Services</h3>
                <p className="mb-6">
                  Luxury chauffeur and transportation services for seamless
                  travel.
                </p>
                <Link to="/Services">
                <button className="bg-white text-[#73BDF2] px-4 py-2 rounded hover:bg-gray-100 transition-colors duration-300 flex items-center">
                  Read More <ChevronRight size={20} className="ml-2" />
                </button>
                </Link>
              </div>
            </div>
          </div>

          {showMore && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8 animate-fadeIn">
              {/* Service 4 */}
              <div className="bg-gradient-to-r from-[#73BDF2] to-[#000000] rounded-lg overflow-hidden shadow-lg transform hover:-translate-y-2 transition-transform duration-300">
                <div className="p-8 text-white">
                  <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <Briefcase size={32} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Baggage Assistance</h3>
                  <p className="mb-6">
                    Professional handling of your luggage from arrival to
                    departure.
                  </p>
                  <Link to="/Services">
                  <button className="bg-white text-[#73BDF2] px-4 py-2 rounded hover:bg-gray-100 transition-colors duration-300 flex items-center">
                    Read More <ChevronRight size={20} className="ml-2" />
                  </button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="text-center mt-10">
            <button
              onClick={() => setShowMore(!showMore)}
              className="border border-[#73BDF2] text-[#73BDF2] px-6 py-3 rounded hover:bg-[#73BDF2]/10 transition-colors duration-300"
            >
              {showMore ? 'Show less' : 'Show me more'}
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gradient-to-r from-[#73BDF2] to-[#000000] text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-1/2 mb-10 md:mb-0 animate-fadeInLeft">
              <h2 className="text-3xl font-bold mb-6">
                Why you Should choose
                <br />
                Moreflex Aviation Support Services
              </h2>
              <p className="mb-8">
                We go beyond the ordinary to provide a stress-free and luxurious
                airport experience. With our exceptional expertise in premium
                lounge access and reliable transportation, we make a committed
                to ensuring your journey is smooth, efficient, and enjoyable.
              </p>

              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                    <span className="text-xl font-bold">05+</span>
                  </div>
                  <p>Year of experience in the aviation industry</p>
                </div>
                <div className="flex items-center">
                  <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                    <span className="text-xl font-bold">135+</span>
                  </div>
                  <p>Countries present</p>
                </div>
                <div className="flex items-center">
                  <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                    <span className="text-xl font-bold">2K+</span>
                  </div>
                  <p>Satisfied Customers</p>
                </div>
              </div>

              <Link to="/Contact">
              <button className="mt-8 bg-white text-[#73BDF2] px-6 py-3 rounded hover:bg-gray-100 transition-colors duration-300 flex items-center">
                Get Started <ChevronRight size={20} className="ml-2" />
              </button>
              </Link>
            </div>

            <div className="md:w-1/2 md:pl-12 animate-fadeInRight">
              <div className="bg-white/10 p-8 rounded-lg backdrop-blur-sm">
                <h3 className="text-xl font-bold mb-6">License & Certifies</h3>
                <div className="space-y-4">
                  <div className="bg-white/10 p-4 rounded-lg">
                    <div className="w-full bg-white/20 h-2 rounded-full">
                      <div className="bg-white h-2 rounded-full w-[95%]"></div>
                    </div>
                    <div className="flex justify-between mt-2">
                      <span>International Aviation Standards</span>
                      <span>95%</span>
                    </div>
                  </div>
                  <div className="bg-white/10 p-4 rounded-lg">
                    <div className="w-full bg-white/20 h-2 rounded-full">
                      <div className="bg-white h-2 rounded-full w-[90%]"></div>
                    </div>
                    <div className="flex justify-between mt-2">
                      <span>Customer Satisfaction</span>
                      <span>90%</span>
                    </div>
                  </div>
                  <div className="bg-white/10 p-4 rounded-lg">
                    <div className="w-full bg-white/20 h-2 rounded-full">
                      <div className="bg-white h-2 rounded-full w-[98%]"></div>
                    </div>
                    <div className="flex justify-between mt-2">
                      <span>Professional Team</span>
                      <span>98%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-[#73BDF2] font-bold">
              <Link to="/about#team">OUR GLOBAL TEAM</Link>
            </p>
            <h2 className="text-3xl font-bold mb-6">
              OVER 600 PROFESSIONALS WORLDWIDE <br />
              DEDICATED TO EXCELLENCE
            </h2>
            <p className="max-w-3xl mx-auto mb-8">
              Our team consists of over 600 highly skilled professionals operating in more than 135 countries.
              From airport concierge specialists to expert chauffeurs and dedicated customer support agents, 
              we ensure seamless travel experiences across the globe. No matter where you are, our team is 
              ready to serve you with precision, efficiency, and a touch of luxury.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link to="/about">
                <button className="bg-[#73BDF2] text-white px-6 py-3 rounded hover:bg-[#5ba8e0] transition-colors duration-300 flex items-center justify-center">
                  Learn More About Us <ChevronRight size={20} className="ml-2" />
                </button>
              </Link>
              <Link to="/contact">
                <button className="border border-[#73BDF2] text-[#73BDF2] px-6 py-3 rounded hover:bg-[#73BDF2]/10 transition-colors duration-300 flex items-center justify-center">
                  Get in Touch <ChevronRight size={20} className="ml-2" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gradient-to-r from-[#73BDF2] to-[#000000]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 text-white">
            <p className="font-bold">TESTIMONIALS</p>
            <h2 className="text-3xl font-bold">
              What Our Satisfied Client Saying In
              <br />
              Their Feedback!
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-[#73BDF2]/20 backdrop-blur-sm p-6 rounded-lg border border-white/20 text-white animate-fadeIn">
              <div className="flex mb-4">
                <div className="text-yellow-300 flex">
                  {'★★★★★'.split('').map((star, index) => (
                    <span key={index}>{star}</span>
                  ))}
                </div>
              </div>
              <p className="mb-6">
                "I can't thank Moreflex Aviation enough for their exceptional
                service. The meet and greet service was flawless, and the
                chauffeur was professional and punctual. Will definitely use
                their services again!"
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-white rounded-full overflow-hidden mr-4">
                  <img
                    src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1473&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80"
                    alt="Mr. Fadil Mohammed Frequent Traveler"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h4 className="font-bold">Mr. Fadil Mohammed</h4>
                  <p className="text-sm">Frequent Traveler</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-[#73BDF2]/20 backdrop-blur-sm p-6 rounded-lg border border-white/20 text-white animate-fadeIn delay-100">
              <div className="flex mb-4">
                <div className="text-yellow-300 flex">
                  {'★★★★★'.split('').map((star, index) => (
                    <span key={index}>{star}</span>
                  ))}
                </div>
              </div>
              <p className="mb-6">
                "The lounge access provided by Moreflex Aviation made my layover
                so much more comfortable. The staff was attentive and the
                baggage assistance saved me so much time and stress. Highly
                recommended!"
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-white rounded-full overflow-hidden mr-4">
                  <img
                    src="https://images.unsplash.com/photo-1598760122223-45f0f18a1bbd?q=80&w=1587&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=688&q=80"
                    alt="Daniel Amuyunzu. Business Executive"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h4 className="font-bold">Daniel Amuyunzu.</h4>
                  <p className="text-sm">Business Executive</p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-[#73BDF2]/20 backdrop-blur-sm p-6 rounded-lg border border-white/20 text-white animate-fadeIn delay-200">
              <div className="flex mb-4">
                <div className="text-yellow-300 flex">
                  {'★★★★★'.split('').map((star, index) => (
                    <span key={index}>{star}</span>
                  ))}
                </div>
              </div>
              <p className="mb-6">
                "As someone who travels frequently for business, Moreflex
                Aviation has become an essential part of my travel routine.
                Their fleet services are top-notch and their staff is always
                professional and courteous."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-white rounded-full overflow-hidden mr-4">
                  <img
                    src="https://images.unsplash.com/photo-1520262494112-9fe481d36ec3?q=80&w=1587&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=761&q=80"
                    alt="Mr. Hughe Evans Corporate traveler"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h4 className="font-bold">Mr. Hughe Evans</h4>
                  <p className="text-sm">Corporate Traveler</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-gray-100 rounded-lg p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#73BDF2] rounded-full opacity-20 transform translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#73BDF2] rounded-full opacity-20 transform -translate-x-1/2 translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-16 h-16 bg-[#73BDF2] rounded-full opacity-20 transform translate-x-1/3 translate-y-1/3"></div>

            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between">
              <div className="md:w-1/2 mb-8 md:mb-0">
                <h2 className="text-2xl font-bold mb-4">
                  Stay Updated with Exclusive Travel Services & Offers!
                </h2>
                <p className="text-gray-600">
                  Subscribe to our newsletter to receive the latest updates
                  about our travel tips and service updates.
                </p>
              </div>
              <div className="md:w-1/2 md:pl-8">
                <NewsletterForm />
                <p className="text-sm text-gray-500 mt-4">
                  By subscribing, you agree to our Privacy Policy and Terms of
                  Service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#000000] text-white pt-16 pb-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div>
                <div className="flex items-center mb-6">
                  <img 
                    src="/Final_logo_6.png" 
                    alt="Moreflex Aviation" 
                    className="h-12 mr-2"
                    loading="lazy"
                  />
                </div>
                <p className="mb-6">
                <Link to="/services">We provide premium airport services</Link> designed to make your <Link to="/services#business-travelers">
                travel</Link> experience seamless, comfortable, and stress-free.
                </p>
                <div className="flex space-x-4">
                <a
                  href="https://facebook.com/yourpage"
                  className="bg-[#73BDF2]/20 w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#73BDF2] transition-colors duration-300 social-icon"
                  aria-label="Follow us on Facebook"
                >
                  <Facebook size={18} />
                </a>
                <a
                  href="https://twitter.com/yourpage"
                  className="bg-[#73BDF2]/20 w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#73BDF2] transition-colors duration-300 social-icon"
                  aria-label="Follow us on Twitter"
                >
                  <Twitter size={18} />
                </a>
                <a
                  href="https://instagram.com/yourpage"
                  className="bg-[#73BDF2]/20 w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#73BDF2] transition-colors duration-300 social-icon"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram size={18} />
                </a>
                <a
                  href="https://linkedin.com/company/yourpage"
                  className="bg-[#73BDF2]/20 w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#73BDF2] transition-colors duration-300 social-icon"
                  aria-label="Connect with us on LinkedIn"
                >
                  <Linkedin size={18} />
                </a>
              </div>
            </div>

            <div>
            <h3 className="text-lg font-bold mb-6">Quick Links</h3>
            <ul className="space-y-4">
              <li>
                <Link to="/" className="hover:text-[#73BDF2] transition-colors duration-300">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-[#73BDF2] transition-colors duration-300">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-[#73BDF2] transition-colors duration-300">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-[#73BDF2] transition-colors duration-300">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-6">Our Services</h3>
            <ul className="space-y-4">
              <li>
                <Link to="/services" className="hover:text-[#73BDF2] transition-colors duration-300">
                  Meet and Greet
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-[#73BDF2] transition-colors duration-300">
                  Lounge Services
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-[#73BDF2] transition-colors duration-300">
                  Fleet Services & Chauffeur
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-[#73BDF2] transition-colors duration-300">
                  Baggage Assistance
                </Link>
              </li>
            </ul>
          </div>

            <div>
              <h3 className="text-lg font-bold mb-6">Contact Us</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <MapPin
                    className="text-[#73BDF2] mr-3 mt-1 flex-shrink-0"
                    size={18}
                  />
                  <span>80100 Mombasa, Kenya</span>
                </li>
                <li className="flex items-center">
                  <Phone
                    className="text-[#73BDF2] mr-3 flex-shrink-0"
                    size={18} />
                  <div className="flex flex-col"> 
                  <span>+254 (114) 660-056</span> 
                  <span>+254 (102) 188-352</span>
                  </div> 
                </li>
                <li className="flex items-center">
                  <Mail
                    className="text-[#73BDF2] mr-3 flex-shrink-0"
                    size={18}
                  />
                  <span>info@moreflexaviation.com</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p>
                &copy; 2026 Moreflex Aviation Support Services. All Rights
                Reserved.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <a
                  href="#"
                  className="hover:text-[#73BDF2] transition-colors duration-300"
                >
                  Privacy Policy
                </a>
                <a
                  href="#"
                  className="hover:text-[#73BDF2] transition-colors duration-300"
                >
                  Terms & Conditions
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
